export type CoachStyle = "analyst" | "sage" | "catalyst";

export type CoachGender = "male" | "female";

const COACH_GENDERS = new Set<string>(["male", "female"]);
const COACH_STYLES = new Set<string>(["analyst", "sage", "catalyst"]);

export type CoachOption = {
  /** Persona slug — used as CMS copy key (settings.coach.options.{id}.*). */
  id: string;
  name: string;
  gender: CoachGender;
  style: CoachStyle;
  /** Populated at runtime by useCoachOptionsCopy from CMS. Not set in COACH_OPTIONS. */
  tone?: string;
  preview?: string;
  "coach-selection-intro"?: string;
  "coach-selection-confirmation"?: string;
};

// Identity only. The id keys the CMS copy (settings.coach.options.{id}.*) that supplies the
// displayed name, tone and preview; gender + style select the persona and the Gemini Live voice
// from cms/maia-echo/voice-styles.
export const COACH_OPTIONS: CoachOption[] = [
  { id: "julian", name: "Julian", gender: "male", style: "analyst" },
  { id: "marius", name: "Marius", gender: "male", style: "sage" },
  { id: "oliver", name: "Oliver", gender: "male", style: "catalyst" },
  { id: "elena", name: "Elena", gender: "female", style: "analyst" },
  { id: "clara", name: "Clara", gender: "female", style: "sage" },
  { id: "maria", name: "Maria", gender: "female", style: "catalyst" },
];

export const DEFAULT_COACH_OPTION = COACH_OPTIONS.find(c => c.id === "marius") ?? COACH_OPTIONS[0];

export function coachToMaiaFallbackVoiceFolder(coach: Pick<CoachOption, "style" | "gender">): string {
  return `${coach.style}_${coach.gender}`;
}

export function findCoachOption(
  coachSound: string | undefined,
  coachStyle: string | undefined,
): CoachOption | undefined {
  if (!coachSound || !coachStyle) return undefined;
  return COACH_OPTIONS.find(c => c.gender === coachSound && c.style === coachStyle);
}

export function resolveCoachFromPreferences(
  coachSound: string | undefined,
  coachStyle: string | undefined,
): CoachOption {
  return findCoachOption(coachSound, coachStyle) ?? DEFAULT_COACH_OPTION;
}

/**
 * Normalizes raw API coach fields into domain preferences.
 * The backend field names are misleading and values may arrive in either orientation,
 * so we detect gender vs persona style by value — not by key name.
 */
export function parseCoachPreferencesFromApi(
  coachSound?: string | null,
  coachStyle?: string | null,
): { coachSound: string | undefined; coachStyle: CoachStyle | undefined } {
  const a = coachSound ?? undefined;
  const b = coachStyle ?? undefined;

  if (!a && !b) return { coachSound: undefined, coachStyle: undefined };

  if (a && b) {
    if (COACH_GENDERS.has(a) && COACH_STYLES.has(b)) {
      return { coachSound: a, coachStyle: b as CoachStyle };
    }
    if (COACH_STYLES.has(a) && COACH_GENDERS.has(b)) {
      return { coachSound: b, coachStyle: a as CoachStyle };
    }
  }

  const gender = [a, b].find(v => v && COACH_GENDERS.has(v));
  const style = [a, b].find(v => v && COACH_STYLES.has(v));
  return { coachSound: gender, coachStyle: style as CoachStyle | undefined };
}

/** Serializes domain coach preferences to API fields (coach_sound = gender, coach_style = persona). */
export function coachPreferencesToApi(
  coachSound: string | undefined,
  coachStyle: string | undefined,
): { coach_sound: string | undefined; coach_style: string | undefined } {
  return {
    coach_sound: coachSound,
    coach_style: coachStyle,
  };
}
